import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const CurrencyContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com';

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState({ currencyCode: 'GBP', currencySymbol: '£', currencyName: 'British Pound' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCurrencyPreference = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/api/currency/currency-preference`);
      setCurrency(response.data);
    } catch (error) {
      console.error('Error fetching currency preference:', error);
      setError('Failed to load currency preference');
      // Fallback to GBP
      setCurrency({ currencyCode: 'GBP', currencySymbol: '£', currencyName: 'British Pound' });
    } finally {
      setLoading(false);
    }
  };

  const updateCurrencyPreference = async (newCurrency) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.put(`${API_URL}/api/currency/currency-preference`, {
        currency: newCurrency
      });
      setCurrency(response.data.currency);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating currency preference:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update currency';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '';

    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (isNaN(numericAmount)) return '';

    // Format with currency symbol and 2 decimal places
    return `${currency.currencySymbol}${numericAmount.toFixed(2)}`;
  };

  const initializeCurrency = async () => {
    try {
      // First, try to seed currencies if they don't exist
      await axios.post(`${API_URL}/api/currency/seed-currencies`);
      // Then create default preferences if they don't exist
      await axios.post(`${API_URL}/api/currency/create-default-preferences`);
      // Finally, fetch the currency preference
      await fetchCurrencyPreference();
    } catch (error) {
      console.error('Error initializing currency:', error);
      // Even if initialization fails, set default currency
      setCurrency({ currencyCode: 'GBP', currencySymbol: '£', currencyName: 'British Pound' });
    }
  };

  useEffect(() => {
    initializeCurrency();
  }, []);

  const value = {
    currency,
    loading,
    error,
    formatAmount,
    updateCurrencyPreference,
    refreshCurrency: fetchCurrencyPreference,
    clearError: () => setError(null)
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};